import React, { useCallback, useEffect, useState } from 'react';
import { Briefcase, Download, User, UserPlus } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  WaitlistEntry,
  WaitlistSegment,
  WaitlistStats,
  waitlistApi,
} from '../../api/waitlist';
import { PaginatedResponse } from '../../types';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';
import { Table } from '../../components/ui/Table';

const PAGE_SIZE = 20;

const segmentLabel: Record<WaitlistSegment, string> = {
  personal: 'Personal',
  business: 'Business',
};

const segmentBadge: Record<WaitlistSegment, 'info' | 'premium'> = {
  personal: 'info',
  business: 'premium',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const muted: React.CSSProperties = { color: 'var(--neutral-500)' };

const filterSelectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  appearance: 'none',
  backgroundImage:
    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M3 4.5L6 7.5L9 4.5\' stroke=\'%23566472\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  cursor: 'pointer',
};

/**
 * Who has joined the marketing site's waitlist, for the Personal and the
 * Business app. Read-only: these are not accounts, and nothing here is
 * verified — an address on this list is only somebody who typed it in.
 */
export const Waitlist: React.FC = () => {
  const toast = useToast();
  const [search, setSearch] = useState('');

  usePage('Waitlist', {
    placeholder: 'Search by email, name, phone or business…',
    onSearch: setSearch,
  });

  const [stats, setStats] = useState<WaitlistStats | null>(null);
  const [data, setData] = useState<PaginatedResponse<WaitlistEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState<WaitlistSegment | ''>('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, listRes] = await Promise.all([
        waitlistApi.stats(),
        waitlistApi.list({
          search: search.trim() || undefined,
          segment: segment || undefined,
          page,
          limit: PAGE_SIZE,
        }),
      ]);
      setStats(statsRes);
      setData(listRes);
    } catch (err) {
      toast.error(err, 'Failed to load the waitlist.');
    } finally {
      setLoading(false);
    }
  }, [page, search, segment, toast]);

  useEffect(() => {
    setPage(1);
  }, [search, segment]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchEntries();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchEntries]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await waitlistApi.exportCsv({
        search: search.trim() || undefined,
        segment: segment || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err, 'Could not export the waitlist.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
          gap: 16,
        }}
      >
        <StatCard label="Total sign-ups" value={stats?.total ?? 0} icon={UserPlus} accent="blue" />
        <StatCard
          label="Personal app"
          value={stats?.bySegment.personal ?? 0}
          icon={User}
          accent="green"
        />
        <StatCard
          label="Business app"
          value={stats?.bySegment.business ?? 0}
          icon={Briefcase}
          accent="purple"
        />
        <StatCard
          label="Last 7 days"
          value={stats?.last7Days ?? 0}
          icon={UserPlus}
          accent="neutral"
        />
      </div>

      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <select
            aria-label="Segment"
            value={segment}
            onChange={(e) => setSegment(e.target.value as WaitlistSegment | '')}
            style={filterSelectStyle}
          >
            <option value="">All apps</option>
            <option value="personal">Personal</option>
            <option value="business">Business</option>
          </select>
          <Button
            variant="secondary"
            size="sm"
            loading={exporting}
            disabled={!data || data.meta.total === 0}
            onClick={() => void handleExport()}
          >
            <Download size={14} style={{ marginRight: 6 }} />
            Export CSV
          </Button>
        </div>

        {loading && !data ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <Table
              data={data.items}
              columns={[
                {
                  key: 'createdAt',
                  header: 'Joined',
                  render: (entry) => (
                    <span style={{ whiteSpace: 'nowrap' }}>{formatDate(entry.createdAt)}</span>
                  ),
                },
                {
                  key: 'fullName',
                  header: 'Name',
                  render: (entry) =>
                    entry.fullName ?? <span style={muted}>—</span>,
                },
                { key: 'email', header: 'Email', render: (entry) => entry.email },
                {
                  key: 'phone',
                  header: 'Phone',
                  render: (entry) => entry.phone ?? <span style={muted}>—</span>,
                },
                {
                  key: 'segment',
                  header: 'App',
                  render: (entry) => (
                    <Badge variant={segmentBadge[entry.segment]}>
                      {segmentLabel[entry.segment]}
                    </Badge>
                  ),
                },
                {
                  key: 'businessName',
                  header: 'Business name',
                  render: (entry) =>
                    entry.businessName ?? <span style={muted}>—</span>,
                },
                {
                  key: 'source',
                  header: 'Source',
                  render: (entry) =>
                    entry.source ? (
                      <span title={entry.referrer ?? undefined}>{entry.source}</span>
                    ) : (
                      <span style={muted}>—</span>
                    ),
                },
              ]}
              emptyMessage={
                search || segment
                  ? 'No sign-ups match your filters'
                  : 'Nobody has joined the waitlist yet'
              }
            />
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>
    </div>
  );
};
